import React, { useState, useCallback, useEffect } from "react";
import PropTypes from "prop-types";
import moment from "moment";
import CommonTable from "./CommonTable";
import { Button } from "@mui/material";
import CustomPaper from "../common/customPaper";
import FilterableContentPanel from "../layout/FilterableContentPanel";
import PaginatedDataTable from "./PaginatedDataTable";
import { DateRangePicker } from "../filters/DateRangePicker";
import Loader from "../common/loader";
import { convertToDateRange } from "../../utils/commonUtils";
import NoDataAvailable from "../common/NoDataAvailable";
import { useResponsiveStyles } from "../../styles/useResponsiveStyles";

const DataSummaryTable = ({
    title,
    initialDateRange,
    maxRows,
    queryHook,
    customFooterElm,
    filters,
    singleDateMode,
    totalText,
    firstColumnWidth,
    isHeaderBold,
    isColumnSticky,
    headerFontSizes,
    stickyColumnsCount,
    range,
    highlightRules,
    customCellColor,
    setHeaderHeight,
    headerHeights,
    headerPadding,
    headerGap,
    exportToExcelQuery,
    customCellRender
}) => {
    const [dateRange, setDateRange] = useState(initialDateRange || {});
    const { getFontSize } = useResponsiveStyles();

    // Update local date range when initialDateRange changes
    useEffect(() => {
        if (initialDateRange?.startDate && initialDateRange?.endDate) {
            setDateRange(initialDateRange);
        }
    }, [initialDateRange]);

    const [isViewAllOpen, toggleViewAll] = useState(false);
    const [visibleColumns, setVisibleColumns] = useState([]);
    const extraParams = {}
    if (title === "Corporate Access Summary") {
        extraParams["isSummary"] = true
    }

    // Create a processed filters object from the filters prop
    const processedFilters = {};
    if (filters && filters.length > 0) {
        filters.forEach(filter => {
            if (filter && filter.key) {
                processedFilters[filter.key] = null;
            }
        });
    }

    const { data, error, isLoading } = queryHook({
        startDate: dateRange.startDate,
        endDate: dateRange.endDate,
        appliedFilters: {
            isSummary: title === "Corporate Access Summary" ? true : null,
            ...processedFilters
        }
    });

    const handleDateRangeChange = useCallback((startDate, endDate) => {
        setDateRange({ startDate, endDate });
    }, []);

    useEffect(() => {
        setVisibleColumns(data?.columns?.map((header) => header.key));
    }, [data]);

    let staticContent;
    if (isLoading) {
        staticContent = <Loader />;
    } else if (error) {
        staticContent = "Error";
    } else if (
        !data ||
        !data.columns ||
        !data?.data?.length ||
        data?.data?.every((row) =>
            Object.keys(row).every(
                (key) =>
                    [
                        "username",
                        "email",
                        "id",
                        "role",
                        "ambit_sector",
                        "report_type",
                        "rank",
                    ].includes(key) || row[key] === 0
            )
        )
    ) {
        staticContent = <NoDataAvailable />;
    }
    const FilterableElm = ({ appliedFilters, filterElmDateRange }) => {
        return (
            <PaginatedDataTable
                appliedFilters={appliedFilters}
                queryHook={queryHook}
                initialDateRange={initialDateRange}
                isPopOver={true}
                filterElmDateRange={filterElmDateRange}
                highlightRules={highlightRules}
                customCellColor={customCellColor}
                range={range}
                headerFontSizes={headerFontSizes}
                headerHeights={headerHeights}
                headerPadding={headerPadding}
                headerGap={headerGap}
                customCellRender={customCellRender}
            />
        );
    };

    const closeDrawer = () => {
        toggleViewAll(false);
    };
    return (
        <CustomPaper
            style={{
                height: "100%",
                minHeight: "250px",
                position: "relative",
                display: "flex",
                flexDirection: "column",
            }}
        >
            <div style={{ flex: 1, display: "flex", flexDirection: "column" }}>
                <div style={{ flex: 1 }}>
                    <div className="text-left flex justify-between items-center mb-2">
                        <h2
                            className="font-bold text-primary"
                            style={{
                                ...getFontSize("sub-heading"),
                                width: "100%",
                            }}
                        >
                            {title}
                        </h2>
                        <DateRangePicker
                            dateRange={dateRange}
                            onDateRangeChange={handleDateRangeChange}
                            range={range}
                        />
                    </div>

                    {staticContent ? (
                        <div
                            style={{
                                ...styles.staticContent,
                                flex: 1,
                            }}
                        >
                            {staticContent}
                        </div>
                    ) : (
                        <CommonTable
                            title={title}
                            headers={data.columns}
                            data={data.data}
                            maxRows={maxRows}
                            totalText={totalText}
                            firstColumnWidth={firstColumnWidth}
                            isHeaderBold={isHeaderBold}
                            isColumnSticky={isColumnSticky}
                            headerFontSizes={headerFontSizes}
                            stickyColumnsCount={stickyColumnsCount}
                            highlightRules={highlightRules}
                            customCellColor={customCellColor}
                            visibleColumns={visibleColumns}
                            setHeaderHeight={setHeaderHeight}
                            headerHeights={headerHeights}
                            headerPadding={headerPadding}
                            headerGap={headerGap}
                            customCellRender={customCellRender}
                        />
                    )}
                </div>

                {!staticContent && (
                    <div
                        style={{
                            ...styles.footer,
                            justifyContent: customFooterElm
                                ? "space-between"
                                : "flex-end",
                        }}
                    >
                        {customFooterElm}
                        <Button
                            variant="text"
                            color="primary"
                            className="view-all-button"
                            onClick={() => toggleViewAll(true)}
                        >
                            View all
                        </Button>
                    </div>
                )}
            </div>

            {isViewAllOpen && (
                <FilterableContentPanel
                    title={title}
                    filterableElm={FilterableElm}
                    filtersContent={filters}
                    isPanelOpen={isViewAllOpen}
                    closeDrawer={closeDrawer}
                    initialDateRange={dateRange}
                    range={range}
                    exportToExcelQuery={exportToExcelQuery}
                />
            )}
        </CustomPaper>
    );
};

const styles = {
    staticContent: {
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        width: "100%",
    },
    footer: {
        position: "relative",
        display: "flex",
        alignItems: "center",
        flexDirection: "row",
    },
};

DataSummaryTable.propTypes = {
    title: PropTypes.string.isRequired,
    initialDateRange: PropTypes.shape({
        startDate: PropTypes.string.isRequired,
        endDate: PropTypes.string.isRequired,
    }).isRequired,
    maxRows: PropTypes.number,
    queryHook: PropTypes.func.isRequired,
    customFooterElm: PropTypes.node,
    singleDateMode: PropTypes.bool,
};

export default DataSummaryTable;
